import { Avatar, Badge, Group, Text } from '@mantine/core'
import { Grid } from '@mantine/core'
import { Select } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import avatars from '@/assets/avatars'
import { useAllEsks } from '@/hooks/useAllEsks'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { getDefaultPskAvatar, getDefaultPskAvatarId } from '@/screens/SetupScreen/utils'
import { isHelpModule } from '@/utils/contentModuleTables'
import { getRequiredFieldDescriptionProps } from '@/utils/forms'

const PSK_VALUE = 'PSK'

export const TopicSettingsSection = observer(function TopicSettingsSection(props: CMFormSection) {
  const { authenticationStore } = useGlobalState()
  const { user } = authenticationStore
  const isSponsorAdmin = user?.isSponsorAdmin ?? false
  const { allEsks, sidekicksQuery } = useAllEsks()

  const selectEskRef = useRef<HTMLInputElement>(null)
  const fieldRefs = useRef({ selectEsk: selectEskRef }).current

  // Expose field refs through props — only needs to run once
  useEffect(() => {
    if (props.setFieldRefs) {
      props.setFieldRefs(fieldRefs as Record<string, React.RefObject<HTMLElement>>)
    }
  }, [])

  // Build sidekick dropdown options with usage counts
  const currentTaskId = props.taskId

  // Store metadata separately (Mantine Select only accepts value/label/disabled/group)
  const sidekickMetadata = useMemo(() => {
    const contentModules = props.contentModulesQuery.data ?? []

    // Count how many CMs use each sidekick (excluding current CM and help modules)
    const usageCounts = new Map<string, number>()
    contentModules
      .filter(cm => cm.taskId !== currentTaskId && !isHelpModule(cm))
      .forEach(cm => {
        if (cm.selectEsk === PSK_VALUE) {
          const count = usageCounts.get(PSK_VALUE) ?? 0
          usageCounts.set(PSK_VALUE, count + 1)
        } else if (cm.selectEsk) {
          const count = usageCounts.get(cm.selectEsk) ?? 0
          usageCounts.set(cm.selectEsk, count + 1)
        }
      })

    // Build metadata map for avatar and usage count lookups
    const metadata = new Map<string, { avatarId: string; usageCount: number }>()

    // PSK metadata
    metadata.set(PSK_VALUE, {
      avatarId: getDefaultPskAvatarId(sidekicksQuery.data),
      usageCount: usageCounts.get(PSK_VALUE) ?? 0,
    })

    // ESK metadata
    allEsks.forEach(e => {
      metadata.set(e.eskId, {
        avatarId: e.avatarId,
        usageCount: usageCounts.get(e.eskId) ?? 0,
      })
    })

    return metadata
  }, [allEsks, props.contentModulesQuery.data, currentTaskId, sidekicksQuery.data])

  // Mantine Select data using proper grouped format
  const sidekickOptions = useMemo(() => {
    const groups: Array<{ group: string; items: Array<{ value: string; label: string }> }> = []

    // Personal Sidekick group
    groups.push({
      group: 'Personal',
      items: [{ value: PSK_VALUE, label: 'Personal Sidekick' }],
    })

    // Expert Sidekicks group (only if there are ESKs)
    if (allEsks.length > 0) {
      groups.push({
        group: 'Expert Sidekicks',
        items: allEsks.map(e => ({
          value: e.eskId,
          label: e.name,
        })),
      })
    }

    return groups
  }, [allEsks])

  // Watch selectEsk so this section re-renders when the sidekick selection
  // changes, not on every keystroke in other fields.
  const [selectedEsk, setSelectedEsk] = useState(() => props.moduleForm.getValues().selectEsk)
  props.moduleForm.watch('selectEsk', ({ value }) => setSelectedEsk(value))

  // Get avatar for the selected sidekick
  const selectedSidekickAvatar = useMemo(() => {
    // Handle PSK selection
    if (selectedEsk === PSK_VALUE) {
      return getDefaultPskAvatar(sidekicksQuery.data)
    }

    if (!selectedEsk) return avatars.placeholder

    const esk = allEsks.find(e => e.eskId === selectedEsk)
    return esk ? avatars[esk.avatarId] : avatars.placeholder
  }, [selectedEsk, allEsks, sidekicksQuery.data])

  const handleSidekickChange = useCallback(
    (value: string | null) => {
      props.moduleForm.setFieldValue('selectEsk', value ?? '')
    },
    [props.moduleForm],
  )

  return (
    <Grid gutter="xxxl">
      {/* Sidekick Selector */}
      <Grid.Col span={{ base: 12, md: isSponsorAdmin ? 6 : 12 }}>
        <Select
          ref={fieldRefs.selectEsk}
          label="Sidekick"
          withAsterisk
          allowDeselect={false}
          disabled={props.isLive}
          styles={{
            label: {
              fontSize: '15px',
            },
          }}
          scrollAreaProps={{
            type: 'always',
            scrollbarSize: 8,
          }}
          data={sidekickOptions}
          value={selectedEsk}
          onChange={handleSidekickChange}
          error={props.moduleForm.errors.selectEsk}
          {...(props.isLive
            ? { description: 'Sidekick cannot be changed while the module is published.' }
            : getRequiredFieldDescriptionProps(props.moduleForm, 'selectEsk', ''))}
          leftSection={selectedEsk ? <Avatar src={selectedSidekickAvatar} size="sm" radius="sm" /> : null}
          leftSectionWidth={selectedEsk ? 40 : undefined}
          renderOption={({ option }) => {
            const metadata = sidekickMetadata.get(option.value)
            const usageCount = metadata?.usageCount ?? 0
            const avatarId = metadata?.avatarId
            return (
              <Group gap="sm" justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
                <Group gap="sm" wrap="nowrap">
                  <Avatar src={avatarId ? avatars[avatarId] : avatars.placeholder} size="sm" radius="sm" />
                  <Text size="sm" c={option.disabled ? 'dimmed' : undefined}>
                    {option.label}
                  </Text>
                </Group>
                {usageCount > 0 && (
                  <Badge size="sm" variant="light" color="blue">
                    {usageCount} module{usageCount === 1 ? '' : 's'}
                  </Badge>
                )}
              </Group>
            )
          }}
        />
      </Grid.Col>
    </Grid>
  )
})
